// Copyright 2025-2026 NXGN Management, LLC. All Rights Reserved.
import { useMemo } from 'react'
import { Flex, StateMessage, Typography } from '@teamnine/pcj-ui-v2'
import { ContentCopy, Input } from '@/utils/material-icons'
import { LightAsync as SyntaxHighlighter } from 'react-syntax-highlighter'
import { docco } from 'react-syntax-highlighter/dist/esm/styles/hljs'
import { useTheme } from 'styled-components'
import jsonHighlighting from 'react-syntax-highlighter/dist/esm/languages/hljs/json'

import { useGetRenderedTemplate } from '@/app/Templates/hooks/useGetRenderedTemplate'
import { PrimaryLoader } from '@/app/shared/components/FullPageLoader'
import { useErrorHelper } from '@/app/shared/hooks/useErrorHelper'
import LinkButton from '@/app/shared/components/LinkButton'

SyntaxHighlighter.registerLanguage('json', jsonHighlighting)

type TemplateJSONProps = {
  templateId: string
  title?: string
  maxHeight?: string
}

export default function TemplateJSON({
  templateId,
  title,
  maxHeight = '60vh',
}: TemplateJSONProps) {
  const { colors } = useTheme()
  const {
    data: renderedTemplate,
    isLoading: loading,
    error,
  } = useGetRenderedTemplate(templateId)
  const { basicErrorHandler, displayGlobalMessage } = useErrorHelper()

  const json = useMemo(() => {
    if (!renderedTemplate) return null

    try {
      return JSON.stringify(renderedTemplate, null, 2)
    } catch (err) {
      console.error('Failed to parse template JSON:', err)
      basicErrorHandler(err)
    }
  }, [renderedTemplate, basicErrorHandler])

  const onCopy = () => {
    if (!json) return
    /* eslint-disable no-undef */
    navigator.clipboard.writeText(json)
    displayGlobalMessage({
      heading: 'Copied to clipboard.',
      type: 'success',
    })
  }

  const onDownload = () => {
    if (!json) return
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `template-${templateId}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  if (error) {
    return (
      <StateMessage.SimpleAxiosError
        title="Error Loading Template JSON"
        error={error}
      />
    )
  }

  if (loading) {
    return (
      <Flex justify="center">
        <PrimaryLoader />
      </Flex>
    )
  }

  if (!json) {
    return (
      <StateMessage.Empty
        title="Template Content Unavailable"
        description="The template content could not be loaded."
      />
    )
  }

  return (
    <Flex direction="column" gap={16}>
      <Flex
        align="center"
        justify={title ? 'space-between' : 'flex-end'}
        gap={16}
      >
        {title && <Typography variant="14-medium">{title}</Typography>}
        <Flex gap={16}>
          <LinkButton
            icon={<ContentCopy sx={{ fontSize: 14 }} />}
            label="Copy to clipboard"
            onClick={onCopy}
          />
          <LinkButton
            icon={<Input sx={{ fontSize: 14 }} />}
            label="Download"
            onClick={onDownload}
          />
        </Flex>
      </Flex>
      <SyntaxHighlighter
        language="json"
        style={docco}
        wrapLongLines
        customStyle={{
          backgroundColor: colors.nggray50,
          borderRadius: '6px',
          maxHeight,
          overflow: 'auto',
        }}
      >
        {json}
      </SyntaxHighlighter>
    </Flex>
  )
}
